/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/phone/QaPhoneModeMenu.tsx
 * FILE_ID: JOBHUB-QA-PHONE-MODEMENU-001
 * PURPOSE: Draggable Build/Run/ITP/Export/More. Stays on screen while the checklist scrolls.
 * Reused from: packages/job-hub/src/jobs/tabs/job-qa/phone/QaPhoneModeBar.tsx (QaPhoneModeBar)
 */
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { FileCheck2, FileOutput, ListChecks, Menu, MoreHorizontal, Table2 } from 'lucide-react'
import { useCloseOnOutsidePointer, useMobileDockRestoreHost } from '@quotech/ui'
import type { QaAreaMode } from '../../JobQaModeToggle'
import { QA_PHONE_TITLE_ACTIONS_SLOT_ID } from '../../../qa/phonePage'
import { QA_PHONE_CONTROLS } from './qaPhoneControlInventory'

const OPEN_KEY = 'qa.phone.modeMenu.open'
const POS_KEY = 'qa.phone.modeMenu.pos.v2'
const BUTTON = 44

const TABS: { mode: QaAreaMode; label: string; icon: typeof Table2; id: string }[] = [
  { mode: 'build', label: 'Build', icon: Table2, id: QA_PHONE_CONTROLS.modeBuild },
  { mode: 'run', label: 'Run', icon: ListChecks, id: QA_PHONE_CONTROLS.modeRun },
  { mode: 'itp', label: 'ITP', icon: FileCheck2, id: QA_PHONE_CONTROLS.modeItp },
]

function readOpen(): boolean {
  try {
    return window.localStorage.getItem(OPEN_KEY) === '1'
  } catch {
    return false
  }
}

function overlapsTitle(pos: { x: number; y: number }): boolean {
  const slot = document.getElementById(QA_PHONE_TITLE_ACTIONS_SLOT_ID)
  if (!slot) return false
  const rect = slot.getBoundingClientRect()
  const right = pos.x + BUTTON
  const bottom = pos.y + BUTTON
  return pos.x < rect.right && right > rect.left && pos.y < rect.bottom && bottom > rect.top
}

function readPos(): { x: number; y: number } | null {
  try {
    const raw = window.localStorage.getItem(POS_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { x?: unknown; y?: unknown }
    if (typeof parsed.x !== 'number' || typeof parsed.y !== 'number') return null
    const pos = { x: parsed.x, y: parsed.y }
    if (overlapsTitle(pos)) return null
    return pos
  } catch {
    return null
  }
}

function defaultPos(): { x: number; y: number } {
  const width = typeof window === 'undefined' ? 390 : window.innerWidth
  const height = typeof window === 'undefined' ? 800 : window.innerHeight
  return { x: width - BUTTON - 8, y: Math.max(8, (height - BUTTON) / 2) }
}

function clamp(x: number, y: number, boxW: number, boxH: number) {
  const width = window.innerWidth
  const height = window.innerHeight
  return {
    x: Math.min(Math.max(8, x), Math.max(8, width - boxW - 8)),
    y: Math.min(Math.max(8, y), Math.max(8, height - boxH - 8)),
  }
}

export function QaPhoneModeMenu({
  mode,
  onModeChange,
  onMore,
  onExport,
  selectedCount,
  onAssign,
  onClearSelection,
  onSaveToLibrary,
  showBuild = true,
}: {
  mode: QaAreaMode
  onModeChange: (mode: QaAreaMode) => void
  onMore: () => void
  onExport: () => void
  selectedCount: number
  onAssign: () => void
  onClearSelection: () => void
  onSaveToLibrary?: () => void
  showBuild?: boolean
}) {
  const tabs = showBuild ? TABS : TABS.filter((tab) => tab.mode !== 'build')
  const dockRestore = useMobileDockRestoreHost()
  const menuRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(readOpen)
  // Reused from: packages/ui/src/hooks/useCloseOnOutsidePointer.ts (useCloseOnOutsidePointer)
  useCloseOnOutsidePointer(open, () => setOpen(false), [menuRef])
  const [pos, setPos] = useState(readPos() ?? defaultPos())
  const drag = useRef<{ x: number; y: number; px: number; py: number; moved: boolean } | null>(null)
  const boxW = open ? 168 : BUTTON
  const boxH = open ? BUTTON + (tabs.length + 2 + (selectedCount > 0 ? 1 : 0) + (dockRestore.visible ? 1 : 0)) * 44 : BUTTON

  useEffect(() => {
    try {
      window.localStorage.setItem(OPEN_KEY, open ? '1' : '0')
    } catch {
      /* private mode */
    }
  }, [open])
  useEffect(() => {
    try {
      window.localStorage.setItem(POS_KEY, JSON.stringify(pos))
    } catch {
      /* private mode */
    }
  }, [pos])
  const pointerHandled = useRef(false)
  const onPointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    drag.current = { x: pos.x, y: pos.y, px: event.clientX, py: event.clientY, moved: false }
    event.currentTarget.setPointerCapture?.(event.pointerId)
  }
  const onPointerMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const start = drag.current
    if (!start) return
    const dx = event.clientX - start.px
    const dy = event.clientY - start.py
    if (Math.hypot(dx, dy) > 6) start.moved = true
    setPos(clamp(start.x + dx, start.y + dy, boxW, boxH))
  }
  const onPointerUp = () => {
    const start = drag.current
    drag.current = null
    pointerHandled.current = true
    if (!start) return
    if (!start.moved) {
      setOpen((value) => !value)
      return
    }
    setPos((current) => (overlapsTitle(current) ? defaultPos() : current))
  }
  const onClick = () => {
    if (pointerHandled.current) {
      pointerHandled.current = false
      return
    }
    setOpen((value) => !value)
  }

  const row = 'flex min-h-[44px] w-full items-center gap-2 px-3 text-left text-sm font-semibold'
  return (
    <div
      ref={menuRef}
      data-testid="qa-phone-mode-menu"
      className="fixed z-40"
      style={{ left: clamp(pos.x, pos.y, boxW, boxH).x, top: clamp(pos.x, pos.y, boxW, boxH).y }}
    >
      <div className={open ? 'w-[168px] rounded-lg border border-border bg-card shadow-md' : ''}>
        <button
          type="button"
          aria-label="Menu"
          aria-expanded={open}
          data-testid="qa-phone-mode-menu-toggle"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onClick={onClick}
          className="flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-card text-foreground"
        >
          {/* Reused from: packages/ui/src/components/Header.tsx (Menu) */}
          <Menu className="h-5 w-5 text-neutral-800 dark:text-neutral-100" aria-hidden />
        </button>
        {open ? (
          <div className="flex flex-col border-t border-border">
            {selectedCount > 0 ? (
              <div data-testid="qa-phone-selection-bar" className="bg-neutral-900 text-white">
                <p className="px-3 pt-2 text-xs tabular-nums">{selectedCount} selected</p>
                <button type="button" onClick={onAssign} className={`${row} text-white`}>Assign</button>
                {onSaveToLibrary ? (
                  <button type="button" onClick={onSaveToLibrary} className={`${row} text-white`}>Save</button>
                ) : null}
                <button type="button" onClick={onClearSelection} className={`${row} text-white`}>Done</button>
              </div>
            ) : null}
            {dockRestore.visible ? (
              <button type="button" onClick={dockRestore.restore} aria-label="Show navigation" data-testid="qa-phone-dock-restore" className={row}>
                Show navigation
              </button>
            ) : null}
            {tabs.map((tab) => {
              const Icon = tab.icon
              const active = mode === tab.mode
              return (
                <button
                  key={tab.mode}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  data-qa-phone-control={tab.id}
                  onClick={() => onModeChange(tab.mode)}
                  className={`${row} ${active ? 'text-primary' : 'text-muted-foreground'}`}
                >
                  <Icon className="h-4 w-4" aria-hidden />
                  {tab.label}
                </button>
              )
            })}
            <button type="button" onClick={onExport} data-qa-phone-control={QA_PHONE_CONTROLS.exportItp} aria-label="Export ITP" className={`${row} text-muted-foreground`}>
              <FileOutput className="h-4 w-4" aria-hidden />
              Export
            </button>
            <button type="button" onClick={onMore} data-testid="qa-phone-more" className={`${row} text-muted-foreground`}>
              <MoreHorizontal className="h-4 w-4" aria-hidden />
              More
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
